import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import STableLayout, { TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import SText from "@/styles/SText";
import { RequirementItem, QuotationRequestStatusEnum, QuotationRequest } from "@/types/requirement";
import { PurchaseOrderStateEnum, SupplierPrice } from "@/types/purchase";
import { addQuotationRequest, handleSelect, QuotationRequestsParams } from "@/apis/requirement";
import { App, Button, Flex, Modal, Progress, Spin } from "antd";
import { quotationPurchase } from "@/apis/quotationRequestItem";
import type { QuotationPurchaseRound } from "@/apis/quotationRequestItem";
import { itemUpdateByKey } from "@/apis/purchase";
import { acceptStateUpdate } from "@/apis/supplier";

export interface ItemWithPrice extends RequirementItem {
  price?: SupplierPrice;
  priceValue?: number;
}

interface GeneratePurchaseProps {
  items: Array<ItemWithPrice>;
  onSuccess?: (items: Array<QuotationRequest>) => void;
  mrd: number | null;
  purchaseRound: QuotationPurchaseRound;
}

const GeneratePurchase: FC<GeneratePurchaseProps> = ({ items, onSuccess, mrd, purchaseRound }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [loading, setLoading] = useState(false);
  const [processNum, setprocessNum] = useState<number>(0);

  const generateQuoAdd = (item: ItemWithPrice): QuotationRequestsParams => {
    const price = item.price;
    return {
      deadline: mrd || 0,
      mrd: mrd || 0,
      supplierPrice: item.priceValue || null,
      supplierTime: mrd ? mrd - 1 : 0,
      mpq: price?.mpq || "1",
      days: price?.days ?? 0,
      ddl: price?.expiredAt ?? null,
      spareCount: "0",
      spareRate: "0",
    };
  };

  const handleGenerate = async () => {
    setLoading(true);
    const successItems: ItemWithPrice[] = [];
    const failedItems: ItemWithPrice[] = [];

    let succQuotations: QuotationRequest[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.id) continue;
      try {
        const params = generateQuoAdd(item);
        let quoList: QuotationRequest[] = [];
        //生成问询单
        quoList = await addQuotationRequest(item.id, QuotationRequestStatusEnum.SUBMITED, params, [item.price?.priceId ?? '']);
        // 生成问询单为空并且已存在了问询单
        if ((!!!quoList || quoList.length === 0) && !!item.quotationRequest) quoList = [item.quotationRequest as QuotationRequest]
        if (quoList && quoList.length > 0) {
          //选中问询单
          await handleSelect(quoList[0].id);
          //去采购【生成采购单】
          let purchasedQuotation: QuotationRequest[] = [];
          purchasedQuotation = await quotationPurchase([quoList[0].id], undefined, purchaseRound);
          //生成采购单为空并且该问询单已有采购单
          if ((!!!purchasedQuotation || purchasedQuotation.length === 0) && !!item.equotationRequest?.purchaseItem) purchasedQuotation = [item.equotationRequest as QuotationRequest]
          if (purchasedQuotation && purchasedQuotation.length > 0) {
            //下单【采购单item的orderState更新为1】
            let purItem = await itemUpdateByKey(purchasedQuotation[0].purchaseItem?.id ?? '', { key: "orderState", value: PurchaseOrderStateEnum.PAYED })
            // //协助接受【采购单acceptStatus更新为1】
            let purOrder = await acceptStateUpdate(purchasedQuotation[0].purchaseItem?.orderId ?? '', 1);
            let resQuuotation: QuotationRequest = {
              ...purchasedQuotation[0],
              purchaseItem: {
                ...purItem,
                order: purOrder
              },
              requirementItem: {
                ...item
              }
            }
            succQuotations.push(resQuuotation);
            successItems.push(item);
            setprocessNum(i + 1);
            continue
          }
        }
        failedItems.push(item);
        setprocessNum(i + 1);
      } catch (error) {
        console.error(`Failed to add quotation request for item ${item.product?.code}:`, error);
        failedItems.push(item);
        setprocessNum(i + 1);
      }
    }

    if (failedItems.length > 0) {
      notification.warning({
        message: `${t("requirement.successGeneratePurchase")} ${succQuotations.length} ${t("requirement.purchaseOrder")},${t("requirement.generatePurchaseFailed")} ${failedItems.length} 个`,
        description: failedItems.map(item => item.product?.code).join(", "),
        placement: "top"
      });
    } else {
      notification.success({
        message: `${t("requirement.successGeneratePurchase")} ${succQuotations.length} ${t("requirement.purchaseOrder")}`,
        placement: "top"
      });
    }

    setLoading(false);
    if (succQuotations.length > 0 && onSuccess) {
      onSuccess(succQuotations);
    }
  };

  useEffect(() => {
    if (items.length > 0) handleGenerate();
  }, [items])

  const columns: ColumnsType<ItemWithPrice> = useMemo(() => [
    {
      title: t("product.code"),
      dataIndex: ['product', 'code'],
      key: 'productCode',
      width: 120,
      render: (_, item) => <SText text={item.product?.code ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.name"),
      dataIndex: ['product', 'name'],
      key: 'productName',
      width: 150,
      render: (_, item) => <SText text={item.product?.name ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.model"),
      dataIndex: ['product', 'model'],
      key: 'productModel',
      width: 120,
      render: (_, item) => <SText text={item.product?.model ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("requirement.requireCount"),
      dataIndex: 'useAmount',
      width: 100,
      render: (_, item) => <SText text={item.useAmount ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("requirement.requirmentQuantity"),
      dataIndex: 'purCount',
      width: 100,
      render: (_, item) => <SText text={item.purCount ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.uom"),
      dataIndex: ['product', 'uom'],
      key: 'uom',
      width: 80,
      render: (_, item) => <SText text={item.product?.uom ?? ""} style={{ width: "100%" }} />,
    },
  ], [t]);

  const headerChildren = (
    <Flex style={{ width: "100%" }}>
      <Button type="primary" loading={loading} onClick={handleGenerate}>{t("requirement.oneClickOrder")}</Button>
    </Flex>
  );

  return (
    <>
      <STableLayout
        headerChildren={headerChildren}
        dataSource={items}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={"max-content"}
        tableLayoutRef={tableRef}
        total={items.length}
        handleChange={() => { }}
        pagination={false}
      />
      <Modal
        title={t('requirement.ordering') + "..."}
        closable={false}
        footer={null}
        maskClosable={false}
        open={loading}
        centered
        width={1000}
      >
        <Flex style={{ marginTop: 120, marginBottom: 100 }} justify="center">
          <Spin spinning={loading} size="large" />
        </Flex>
        <Progress percent={Number((processNum / items.length * 100).toFixed(2))} />
        <span>{`${t("requirement.processed")}${processNum}/${items.length}`}</span>
      </Modal>
    </>
  );
};

export default GeneratePurchase;
